import React from "react";
import "./Portfolio.css";

const EMAIL = "beth.lewis333@gmail.com";
const LINKEDIN = "https://www.linkedin.com/in/beth-t-lewis/";

const jobs = [
  {
    title: "Senior Software Engineer",
    meta: "Varsity Tutors · Apr 2024 — Present",
    points: [
      "Conducted technical screenings for senior software engineer candidates and mentored new team members by answering questions, debugging, performing code reviews, and providing feedback.",
      "Owned and led development of a tool that lets sales agents report technical issues while automatically collecting comprehensive diagnostics data, building the backend infrastructure, coordinating multiple engineers, and facilitating cross team solutions when blockers emerged.",
      "Led key aspects of a communications platform migration from Genesys to Twilio Flex. Built a new system for scheduled callbacks, on-demand calling, and coordinated cross-functional collaboration to smoothly deliver an improved call centre experience to sales agents.",
    ],
  },
  {
    title: "Software Engineer II",
    meta: "Varsity Tutors · Apr 2023 — Apr 2024",
    points: [
      "Provided assistance across engineering teams in meeting deadlines by developing an FAQ-based AI chatbot, saving managers 2+ hours daily.",
      "Developed a full-stack, self-service tool enabling managers to control product availability for agents during customer calls based on specific audience segments, resulting in the acquisition of 440 new clients annually.",
      "Directed a team of engineers across multiple services to build a system for managing unknown inbound customer calls, improving lead conversion and second call rates.",
      "Led a project to transform customer email communications using SendGrid dynamic templates. Provided clear documentation, empowering administrators to update and customize email templates without engineering support.",
    ],
  },
  {
    title: "Software Engineer I",
    meta: "Varsity Tutors · Jan 2020 — Sept 2020 · Jan 2022 — Apr 2023",
    points: [
      "Developed a Call Center application, improving 200+ sales consultants' experience with dynamic call scripts, projected to increase sales conversion rates by 35%.",
      "Contributed to development of client facing websites, updating legacy code and building reusable UI components.",
      "Collaborated with designers and stakeholders to enhance user-friendliness and exchange ideas.",
    ],
  },
  {
    title: "Quality Assurance Analyst",
    meta: "RevenueWire · May 2019 — Jul 2019",
    points: [
      "Worked full-time testing new software features, demonstrating my attention to detail and passion for quality. Created and executed test plans, reported bugs, assisted developers.",
    ],
  },
];

const skills = [
  "Golang",
  "TypeScript",
  "JavaScript",
  "Python",
  "Ruby on Rails",
  "Java",
  "React",
  "Next.js",
  "Vite",
  "Node.js",
  "Tailwind CSS",
  "CSS",
  "GraphQL",
  "RESTful APIs",
  "Microservices",
  "Architecture design",
  "AWS",
  "Serverless",
  "Lambda",
  "S3 & CloudFront",
  "Route 53",
  "RDS",
  "SQS",
  "DynamoDB",
  "SQL",
  "CI/CD",
  "Git",
  "Vitest & Jest",
  "Twilio",
  "SendGrid",
  "AI",
];

const petCareStack = [
  "React 19",
  "Vite 6",
  "Tailwind CSS",
  "Vitest",
  "AWS S3",
  "CloudFront",
  "Route 53",
];

const quantumStack = ["Python", "Qiskit", "Jupyter", "Linear algebra"];

const quantumTopics = [
  "Linear algebra foundations",
  "Running circuits with Qiskit",
  "Teleportation & superdense coding",
  "Deutsch-Jozsa & Grover's search",
];

const Portfolio = () => (
  <div className="portfolio">
    <aside className="portfolio-aside">
      <div>
        <div className="portfolio-name">
          Beth
          <br />
          Lewis
        </div>
        <div className="portfolio-tagline">
          Senior full stack software developer, Montréal QC.
        </div>
      </div>
      <nav className="portfolio-nav">
        <a href="#about">About</a>
        <a href="#work">Experience</a>
        <a href="#projects">Projects</a>
        <a href="#skills">Skills</a>
        <a href="#contact">Contact</a>
      </nav>
      <div className="portfolio-aside-contact">
        <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
        <a href={LINKEDIN} target="_blank" rel="noreferrer">
          LinkedIn
        </a>
      </div>
    </aside>

    <main className="portfolio-main">
      <section id="about" className="portfolio-intro">
        <h1>Senior full stack software developer</h1>
        <p>
          Over 5 years of professional experience in full-stack development —
          call-centre platforms, internal tooling, and client-facing products.
          Seeking a new opportunity to leverage my existing skillset while
          embracing continuous learning.
        </p>
      </section>

      <section id="work" className="portfolio-section">
        <h2 className="portfolio-eyebrow">Experience</h2>
        {jobs.map((job) => (
          <article className="portfolio-job" key={job.title + job.meta}>
            <div className="portfolio-job-head">
              <h3>{job.title}</h3>
              <span className="portfolio-job-dates">{job.meta}</span>
            </div>
            {job.points.length === 1 ? (
              <p>{job.points[0]}</p>
            ) : (
              <ul>
                {job.points.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            )}
          </article>
        ))}
      </section>

      <section id="projects" className="portfolio-project-section">
        <h2 className="portfolio-eyebrow">Selected projects</h2>

        <div className="portfolio-project">
          <div>
            <h3>Jessie&rsquo;s Pet Care</h3>
            <p>
              A single-page marketing site for a pet care business in the
              Halifax area. Bookings happen off-site, so it stays static end to
              end — no state, no router, no backend.
            </p>
            <div className="portfolio-tags">
              {petCareStack.map((tag) => (
                <span className="portfolio-tag" key={tag}>
                  {tag}
                </span>
              ))}
            </div>
            <a
              className="portfolio-button"
              href="https://www.jessiespetcare.ca/"
              target="_blank"
              rel="noreferrer"
            >
              Visit site →
            </a>
          </div>
          <div className="portfolio-deploy">
            <div className="portfolio-deploy-title">Deployment</div>
            <div className="portfolio-pipeline">
              <span>Vite build</span>
              <span>→</span>
              <span>S3 (private)</span>
              <span>→</span>
              <span>CloudFront</span>
              <span>→</span>
              <span>Route 53</span>
              <span>→</span>
              <span>jessiespetcare.ca</span>
            </div>
            <p className="portfolio-panel-note">
              Private S3 bucket reachable only through CloudFront, with an ACM
              certificate on the apex and www.
            </p>
          </div>
        </div>

        <div className="portfolio-project" style={{ marginTop: 32 }}>
          <div>
            <h3>Quantum Computing Notebook</h3>
            <p>
              A self-directed dive into quantum computing, written up as a
              Jupyter notebook: the linear algebra needed to start, running
              circuits with Qiskit, and walkthroughs of the core algorithms.
            </p>
            <div className="portfolio-tags">
              {quantumStack.map((tag) => (
                <span className="portfolio-tag" key={tag}>
                  {tag}
                </span>
              ))}
            </div>
            <a className="portfolio-button" href="/projects/notebook">
              Read the paper →
            </a>
          </div>
          <div className="portfolio-deploy">
            <div className="portfolio-deploy-title">Contents</div>
            <ul className="portfolio-topics">
              {quantumTopics.map((topic) => (
                <li key={topic}>{topic}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section id="skills" className="portfolio-skills-section">
        <h2 className="portfolio-eyebrow">Skills</h2>
        <p className="portfolio-skills">{skills.join(" · ")}</p>
        <div className="portfolio-education">
          <div className="portfolio-education-label">Education</div>
          <div className="portfolio-education-value">
            Bachelor of Science, Computer Science — University of Victoria
          </div>
        </div>
      </section>

      <section id="contact" className="portfolio-contact-section">
        <div className="portfolio-contact">
          <h2>Open to new opportunities.</h2>
          <div className="portfolio-contact-links">
            <a className="portfolio-contact-primary" href={`mailto:${EMAIL}`}>
              {EMAIL}
            </a>
            <a
              className="portfolio-contact-secondary"
              href={LINKEDIN}
              target="_blank"
              rel="noreferrer"
            >
              LinkedIn
            </a>
          </div>
        </div>
      </section>
    </main>
  </div>
);

export default Portfolio;
